import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Bookmark, Plus, Trash2, X, Sparkles, Star, Edit2, Check } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  { name: "Momentum Breakout",   emoji: "🚀", filters: { market: "stocks",   asset_types: ["large_cap", "tech"], timeframe: "1d",  indicators: ["ema_bull_stack", "high_rvol", "macd_bull_cross"], signal_strength: "medium", direction: "bullish" } },
  { name: "High RSI Reversal",   emoji: "↩️", filters: { market: "stocks",   asset_types: ["large_cap"],         timeframe: "1d",  indicators: ["rsi_overbought", "macd_bear_cross", "bb_upper_touch"], signal_strength: "medium", direction: "bearish" } },
  { name: "Oversold Bounce",     emoji: "🔄", filters: { market: "stocks",   asset_types: ["large_cap", "small_cap"], timeframe: "1d", indicators: ["rsi_oversold", "bb_lower_touch", "above_ema9"], signal_strength: "medium", direction: "bullish" } },
  { name: "VWAP Bounce",         emoji: "🎯", filters: { market: "stocks",   asset_types: ["large_cap", "tech"], timeframe: "15m", indicators: ["vwap_bounce", "high_rvol", "low_rsi"], signal_strength: "medium", direction: "bullish" } },
  { name: "MACD Cross",          emoji: "✂️", filters: { market: "stocks",   asset_types: ["large_cap"],         timeframe: "1d",  indicators: ["macd_bull_cross", "above_ema9"], signal_strength: "medium", direction: "bullish" } },
  { name: "Earnings Momentum",   emoji: "📈", filters: { market: "stocks",   asset_types: ["large_cap", "tech"], timeframe: "1d",  indicators: ["ema_bull_stack", "high_rvol", "volume_spike"], signal_strength: "strong", direction: "bullish" } },
  { name: "High Relative Volume", emoji: "🔥", filters: { market: "stocks",  asset_types: ["large_cap", "small_cap"], timeframe: "1d", indicators: ["high_rvol", "volume_spike"], signal_strength: "medium", direction: null } },
  { name: "Tech Runner",         emoji: "💻", filters: { market: "stocks",   asset_types: ["tech"],              timeframe: "1d",  indicators: ["ema_bull_stack", "macd_bull_cross", "high_rvol"], signal_strength: "strong", direction: "bullish" } },
  { name: "ETF Momentum",        emoji: "📊", filters: { market: "etfs",     asset_types: ["index_etf", "sector_etf"], timeframe: "1d", indicators: ["above_ema9", "macd_bull_cross"], signal_strength: "medium", direction: "bullish" } },
  { name: "Scalper Mode",        emoji: "⚡", filters: { market: "stocks",   asset_types: ["tech"],              timeframe: "5m",  indicators: ["vwap_bounce", "high_rvol", "macd_bull_cross"], signal_strength: "strong", direction: null } },
];

export default function SavedScans({ currentFilters, onLoad, openSignal }) {
  const [scans, setScans] = useState([]);
  const [showSave, setShowSave] = useState(false);
  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState("📡");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/api/scans");
      setScans(data);
    } catch (e) { /* not logged in — leave scans empty */
      console.debug("[SavedScans] load skipped:", e?.response?.status);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (openSignal) setShowSave(true); }, [openSignal]);

  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api/scans", { name: name.trim(), emoji, filters: currentFilters });
      setScans([data, ...scans]);
      setShowSave(false); setName("");
      toast.success(`Saved "${data.name}"`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save scan");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/api/scans/${id}`);
      setScans(scans.filter((s) => s.scan_id !== id));
      toast.success("Scan removed");
    } catch (err) { toast.error("Failed to remove"); }
  };

  const toggleFav = async (s) => {
    try {
      const { data } = await api.patch(`/api/scans/${s.scan_id}`, { favorite: !s.favorite });
      setScans(scans.map((x) => x.scan_id === data.scan_id ? data : x).sort((a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0)));
    } catch (e) { toast.error("Failed"); }
  };

  const saveRename = async (s) => {
    if (!editName.trim()) { setEditing(null); return; }
    try {
      const { data } = await api.patch(`/api/scans/${s.scan_id}`, { name: editName.trim() });
      setScans(scans.map((x) => x.scan_id === data.scan_id ? data : x));
      setEditing(null);
      toast.success("Renamed");
    } catch (e) { toast.error("Failed"); }
  };

  const applyPreset = (p) => { onLoad({ ...p.filters }); toast.success(`Loaded "${p.name}"`); };

  return (
    <div className="card-surface p-4 fade-in" data-testid="saved-scans">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bookmark className="w-3.5 h-3.5 text-white/70" />
          <div>
            <div className="label-mono">My Scans</div>
            <h3 className="text-white text-[13px] font-semibold tracking-tight mt-0.5">{scans.length} saved</h3>
          </div>
        </div>
        <button onClick={() => setShowSave(true)} className="btn-ghost text-[11px] py-1.5 px-2.5 flex items-center gap-1" data-testid="save-scan-btn">
          <Plus className="w-3 h-3" /> Save
        </button>
      </div>

      <div className="space-y-1.5">
        {scans.map((s) => (
          <div key={s.scan_id} className="group flex items-center justify-between gap-2 px-2.5 py-2 rounded-xl bg-white/[0.025] border border-white/5 hover:border-white/15 hover:bg-white/[0.05] transition-all" data-testid={`saved-scan-${s.scan_id}`}>
            <div className="flex items-center gap-2 min-w-0 cursor-pointer flex-1" onClick={() => editing !== s.scan_id && onLoad(s.filters)}>
              <span className="text-base shrink-0">{s.emoji || "📡"}</span>
              <div className="min-w-0 flex-1">
                {editing === s.scan_id ? (
                  <input
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => { if (e.key === "Enter") saveRename(s); if (e.key === "Escape") setEditing(null); }}
                    onBlur={() => saveRename(s)}
                    className="w-full bg-black/40 border border-white/15 rounded px-2 py-0.5 text-[12px] text-white outline-none"
                    data-testid={`saved-scan-rename-input-${s.scan_id}`}
                  />
                ) : (
                  <>
                    <div className="text-white text-[12px] font-medium tracking-tight truncate flex items-center gap-1">
                      {s.favorite && <Star className="w-2.5 h-2.5 text-[var(--bull)] fill-[var(--bull)]" />}
                      {s.name}
                    </div>
                    <div className="label-mono mt-0.5 truncate">
                      {s.filters?.timeframe?.toUpperCase()} · {s.filters?.indicators?.length || 0} ind
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={(e) => { e.stopPropagation(); toggleFav(s); }} className="text-white/40 hover:text-[var(--bull)] p-1" data-testid={`saved-scan-fav-${s.scan_id}`} title="Favorite">
                <Star className={`w-3 h-3 ${s.favorite ? "fill-[var(--bull)] text-[var(--bull)]" : ""}`} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); setEditing(s.scan_id); setEditName(s.name); }} className="text-white/40 hover:text-white p-1" data-testid={`saved-scan-edit-${s.scan_id}`} title="Rename">
                <Edit2 className="w-3 h-3" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); remove(s.scan_id); }} className="text-white/40 hover:text-[var(--bear)] p-1" data-testid={`saved-scan-remove-${s.scan_id}`} title="Delete">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}

        <div className="label-mono text-white/40 pt-2 pb-1">{scans.length ? "Quick presets" : "Try a preset"}</div>
        {PRESETS.map((p) => (
          <div key={p.name} onClick={() => applyPreset(p)} data-testid={`preset-${p.name.replace(/\s+/g, "-")}`}
            className="flex items-center gap-2 px-2.5 py-2 rounded-xl bg-white/[0.02] border border-white/5 hover:border-white/15 hover:bg-white/[0.05] transition-all cursor-pointer">
            <span className="text-base">{p.emoji}</span>
            <div className="min-w-0 flex-1">
              <div className="text-white text-[12px] font-medium tracking-tight truncate">{p.name}</div>
              <div className="label-mono mt-0.5">{p.filters.timeframe?.toUpperCase()} · {p.filters.indicators.length} ind · {p.filters.direction || "any"}</div>
            </div>
          </div>
        ))}
      </div>

      {showSave && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-md flex items-center justify-center p-4" onClick={() => !busy && setShowSave(false)} data-testid="save-scan-modal">
          <div onClick={(e) => e.stopPropagation()} className="glass-strong rounded-2xl p-6 w-full max-w-sm relative fade-in">
            <button onClick={() => setShowSave(false)} className="absolute top-3 right-3 text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[var(--bull)]" /><div className="label-mono">Save scan</div></div>
            <h3 className="text-xl text-white font-semibold tracking-tight mt-1">Name this preset</h3>
            <div className="mt-5 space-y-3">
              <div className="flex gap-2">
                <input value={emoji} onChange={(e) => setEmoji(e.target.value.slice(0, 2))} className="input-glass text-center text-lg" style={{ width: 56 }} data-testid="save-emoji-input" />
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. MNQ Scalper" className="input-glass" data-testid="save-name-input" autoFocus />
              </div>
              <button disabled={busy || !name.trim()} onClick={save} className="btn-accent w-full" data-testid="save-confirm-btn">
                {busy ? "Saving…" : "Save scan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
